import {
  WSButton,
  WSButtons,
  WSCircle,
  WSElement,
  WSElementProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayoutPreferences } from "@wingspanhq/payments/dist/interfaces";
import { IGrowthSourceNames } from "@wingspanhq/users/dist/lib/interfaces";
import React from "react";
import { useHistory } from "react-router-dom";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import { useUserId } from "../../query/hooks/helpers";
import {
  useInstantPayoutQuery,
  usePayoutSettings
} from "../../query/payments/queries";
import { useEnterpriseQuery } from "../../query/platform/queries";
import { useAccounts, useActivities } from "../../query/users/queries";
import { getPayoutAccount } from "../../query/users/selectors";
import { WSQueries } from "../../query/WSQuery";
import { PaymentsSetupLayout } from "../components/PaymentsSetupLayout/PaymentsSetupLayout";
import { INVOICE_ACCOUNT_NAME } from "../utils";

export const InvoicesSetupComplete: React.FC<SetupFlowRouteComponentProps> = () => {
  const userId = useUserId();
  const activityQuery = useActivities(userId);
  const payoutSettingsQuery = usePayoutSettings(userId);
  const accountsQuery = useAccounts();
  const instantPayoutQuery = useInstantPayoutQuery({ retry: false });
  const enterpriseQuery = useEnterpriseQuery();
  const history = useHistory();
  const enterprise = enterpriseQuery.data;

  return (
    <PaymentsSetupLayout>
      <WSQueries
        queries={{
          activityQuery,
          accountsQuery,
          payoutSettingsQuery
        }}
      >
        {({
          activityQuery: { data: activity },
          payoutSettingsQuery: { data: payoutSettings },
          accountsQuery: { data: accounts }
        }) => {
          const isEnterprise =
            activity.context?.growthSource === IGrowthSourceNames.Enterprise;

          const payoutAccount = getPayoutAccount(payoutSettings, accounts);
          const instantPayoutAccount =
            instantPayoutQuery.data?.instantPayoutAccount;

          const depositAccountDetailsText =
            payoutSettings.payoutPreferences === PayoutPreferences.Instant &&
            instantPayoutAccount
              ? `Your payments will be deposited to your ${instantPayoutAccount.numbers.brand} Card (${instantPayoutAccount.numbers.last4}) instantly.`
              : payoutAccount && payoutAccount.name === INVOICE_ACCOUNT_NAME
              ? `Your payments will be deposited into your direct deposit (${payoutAccount.numbers.account.slice(
                  -4
                )}) account via direct deposit (ACH).`
              : payoutAccount
              ? `Your payments will be deposited into your ${payoutAccount.name} (${payoutAccount.mask}) account via diect deposit (ACH).`
              : "Your payments will be deposited to your Wingspan Wallet.";

          return (
            <>
              {isEnterprise && enterprise ? (
                <>
                  <WSText.Heading2 mb="M" data-testid="heading">
                    You’re set up to receive payments from {enterprise.name}
                  </WSText.Heading2>

                  <WSText mb="S">{depositAccountDetailsText}</WSText>

                  <WSText mb="L">
                    {enterprise.name}'s partnership with Wingspan provides you
                    with free and optional benefits like tax withholding, income
                    tracking, identifying tax write-offs, and more.
                  </WSText>
                </>
              ) : (
                <>
                  <WSText.Heading2 mb="XS" data-testid="heading">
                    You’re ready to receive payments
                  </WSText.Heading2>

                  <WSText mb="S">{depositAccountDetailsText}</WSText>

                  <WSText mb="L">
                    Wingspan provides you with free benefits like tax
                    withholding, income tracking, identifying tax write-offs,
                    and more.
                  </WSText>
                </>
              )}

              <TaxesText mb="L" />

              <WSButtons forceFullWidth>
                <WSButton
                  name="calculateTaxRate"
                  onClick={() => {
                    history.push("/member/taxes/onboarding/filing-status");
                  }}
                >
                  Calculate estimated tax rate
                </WSButton>
                <WSButton.Secondary
                  name="exploreWingspan"
                  onClick={() => {
                    history.push("/member/dashboard");
                  }}
                >
                  Explore Wingspan’s benefits
                </WSButton.Secondary>
              </WSButtons>
            </>
          );
        }}
      </WSQueries>
    </PaymentsSetupLayout>
  );
};

const TaxesText: React.FC<WSElementProps> = props => (
  <WSElement {...props}>
    <WSCircle.Icon icon="taxProfile" mb="XS" />
    <WSText.Heading4 mb="XS">
      Wingspan can set aside for taxes for free.
    </WSText.Heading4>
    <WSText>
      Automatically set aside for taxes, every time you get paid. Pay your
      estimated taxes in one click. Withdraw or stop withholding at anytime.
    </WSText>
  </WSElement>
);
